import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { ICONS } from '@/constants/icons';
import i18n from '@/i18n';
// Import diretti ai componenti, non via `@ui`: il barrel ri-esporta questo
// stesso modale, quindi passare da lì creerebbe un ciclo (import/no-cycle).
import AppButton from '@/shared/ui/buttons/AppButton';
import AppModal from '@/shared/ui/dialogs/AppModal';
import Empty from '@/shared/ui/Empty';
import Icon from '@/shared/ui/icon/Icon';
import Spinner from '@/shared/ui/spinner';

import styles from './import.module.scss';
import ImportFileStep from './ImportFileStep';
import ImportMappingStep from './ImportMappingStep';
import { applyMapping, guessMapping } from './importSchema';
import { isWorkbookFile, parseImportFile } from './parseImportFile';

const PREVIEW_ROWS = 20;

/**
 * Import generico in tre passi: **file** → **mappatura** → **anteprima**.
 *
 * A differenza dei parser dedicati (`shared/import_xls`, `fleet/_shared/import`)
 * qui le colonne non sono cablate: il file viene letto in forma tabellare e
 * l'utente associa ogni campo dello schema a una colonna, a un valore costante
 * o a "ignora". L'auto-mapping propone la scelta più probabile.
 *
 * L'anteprima è una tabella dei valori mappati: gli import che devono farci
 * interagire l'operatore (risolvere un'anagrafica, raggruppare righe) non usano
 * questo modale ma compongono direttamente `ImportFileStep` e
 * `ImportMappingStep` — vedi `daily_trips/import/ImportDailyTripsModal`.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {import('./importSchema').ImportField[]} props.schema
 * @param {string} props.title
 * @param {(rows: Object[]) => Promise<{created?: number, updated?: number, skipped?: number, errors?: Array}>} props.onImport
 * @param {React.ReactNode} [props.extraFields] campi specifici del dominio (es. il
 *   gestore di destinazione) mostrati nel passo di selezione file.
 * @param {string} [props.importBlockedReason] se valorizzato, blocca l'import e
 *   spiega perché: evita di far scoprire all'utente un requisito mancante solo
 *   dall'errore del backend a fine flusso.
 */
export default function ImportMappingModal({
  open,
  onClose,
  schema,
  title,
  onImport,
  extraFields = null,
  importBlockedReason = '',
}) {
  const [step, setStep] = useState('file');
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { columns, rows, meta, sheets, sheetName, workbook }
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep('file');
    setBusy(false);
    setFile(null);
    setParsed(null);
    setMapping({});
    setResult(null);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const loadFile = async (selected, opts = {}) => {
    setBusy(true);
    try {
      const data = await parseImportFile(selected, opts);
      if (!data.rows.length) {
        toast.info(
          i18n.t('import_nessuna_riga', { defaultValue: 'Nessuna riga trovata nel file' })
        );
      }
      setParsed(data);
      setMapping(guessMapping(schema, data.columns));
      setStep(data.rows.length ? 'mapping' : 'file');
    } catch (err) {
      const messages = {
        invalid_json: i18n.t('import_json_non_valido', { defaultValue: 'JSON non valido' }),
        invalid_shape: i18n.t('import_struttura_non_valida', {
          defaultValue: 'Il file non contiene un elenco di record',
        }),
      };
      toast.error(
        messages[err.message] ||
          i18n.t('errore_lettura_file', { defaultValue: 'Errore nella lettura del file' })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    loadFile(selected);
  };

  const handleSheetChange = (sheetName) => {
    if (!file) return;
    loadFile(file, { sheetName, workbook: parsed?.workbook });
  };

  // Stato derivato: ricalcolato durante il render, niente useEffect + setState.
  const preview = parsed
    ? applyMapping(parsed.rows, schema, mapping)
    : { valid: [], validIndex: [], invalid: [] };

  const handleImport = async () => {
    setBusy(true);
    try {
      const outcome = await onImport(preview.valid);
      setResult(outcome || {});
      setStep('done');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || i18n.t('errore_generico', { defaultValue: 'Errore' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={close} title={title} icon={ICONS.import} maxWidth="lg">
      <Spinner visible={busy} />

      <div className="form-stack">
        {step === 'file' && (
          <ImportFileStep extraFields={extraFields} onFile={handleFile} disabled={busy} />
        )}

        {step === 'mapping' && parsed && (
          <ImportMappingStep
            schema={schema}
            parsed={parsed}
            mapping={mapping}
            onChange={(key, rule) => setMapping((prev) => ({ ...prev, [key]: rule }))}
            onSheetChange={handleSheetChange}
            showSheetSelect={isWorkbookFile(file)}
          />
        )}

        {step === 'preview' && parsed && (
          <>
            {importBlockedReason && (
              <div className="form-error-inline" role="alert">
                <Icon name="error" /> {importBlockedReason}
              </div>
            )}
            <p className={styles.hint}>
              <Icon name="info" />
              {i18n.t('import_riepilogo', {
                defaultValue: '{{valid}} righe valide, {{invalid}} scartate',
                valid: preview.valid.length,
                invalid: preview.invalid.length,
              })}
            </p>

            {preview.valid.length > 0 && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {schema.map((f) => (
                        <th key={f.key}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.valid.slice(0, PREVIEW_ROWS).map((row, i) => (
                      <tr key={i}>
                        {schema.map((f) => (
                          <td key={f.key}>{row[f.key] ?? '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.invalid.length > 0 && (
              <div className={styles.errorList}>
                <div className={styles.errorTitle}>
                  <Icon name="warning" />
                  {i18n.t('import_righe_scartate', { defaultValue: 'Righe scartate' })}
                </div>
                <ul>
                  {preview.invalid.slice(0, PREVIEW_ROWS).map((e) => (
                    <li key={e.index}>
                      {i18n.t('import_riga', { defaultValue: 'Riga' })}{' '}
                      {parsed.meta?.[e.index]?.sourceRow ?? e.index + 2}: {e.errors.join(' · ')}
                    </li>
                  ))}
                </ul>
                {preview.invalid.length > PREVIEW_ROWS && (
                  <span className={styles.hint}>
                    {i18n.t('import_altre_righe', {
                      defaultValue: '… e altre {{count}}',
                      count: preview.invalid.length - PREVIEW_ROWS,
                    })}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {step === 'done' && result && (
          <Empty
            icon={ICONS.success}
            title={i18n.t('import_completato', { defaultValue: 'Import completato' })}
            message={i18n.t('import_esito', {
              defaultValue: '{{created}} creati, {{updated}} aggiornati, {{skipped}} ignorati',
              created: result.created ?? 0,
              updated: result.updated ?? 0,
              skipped: result.skipped ?? 0,
            })}
          />
        )}

        <div className="tab-actions">
          {step === 'preview' && (
            <AppButton
              mode="ghost"
              variant="neutral"
              icon={ICONS.arrowLeft}
              onClick={() => setStep('mapping')}
              disabled={busy}
            >
              {i18n.t('indietro', { defaultValue: 'Indietro' })}
            </AppButton>
          )}
          <AppButton
            mode="ghost"
            variant="neutral"
            icon={ICONS.close}
            onClick={close}
            disabled={busy}
          >
            {step === 'done' ? i18n.t('chiudi', { defaultValue: 'Chiudi' }) : i18n.t('annulla')}
          </AppButton>
          {step === 'mapping' && (
            <AppButton
              mode="nav"
              variant="primary"
              icon={ICONS.arrowRight}
              onClick={() => setStep('preview')}
            >
              {i18n.t('avanti', { defaultValue: 'Avanti' })}
            </AppButton>
          )}
          {step === 'preview' && (
            <AppButton
              mode="nav"
              variant="primary"
              icon={ICONS.import}
              onClick={handleImport}
              loading={busy}
              disabled={preview.valid.length === 0 || !!importBlockedReason}
            >
              {i18n.t('importa', { defaultValue: 'Importa' })} ({preview.valid.length})
            </AppButton>
          )}
        </div>
      </div>
    </AppModal>
  );
}
